// @ts-nocheck
import * as React from "react";
import { Ic, useCurrentUser, AppFooterCompact, siteName, brandParts, supportPhone, fmtDuration } from "./common";
import { MobileNavMenu } from "./public-pages";

// Mobile landing — 390×844 PWA viewport.
// Sections (top → bottom):
//   1. status bar + brand bar
//   2. hero  (headline · subhead · live stats)
//   3. orange hero card (today's visit / generic AI CTA)
//   4. quick-action grid (4 tiles)
//   5. primary CTA + login hint
//   6. features carousel (horizontal scroll)
//   7. "how it works" 3-step
//   8. mini live map preview (live points from /api/v1/map/points)
//   9. partners / trust strip
//  10. FAQ accordion
//  11. secondary CTA + legal links
//  12. bottom nav + home indicator

const CAT_COLOR = { hospital: "rose", health: "green", fire: "orange", welfare: "sky", care: "amber" };

function LandingMobile() {
  const { user } = useCurrentUser();
  const [stats, setStats] = React.useState(null);
  const [guardian, setGuardian] = React.useState(null);
  const [mapData, setMapData] = React.useState(null);
  const [faq, setFaq] = React.useState(0);

  React.useEffect(() => {
    fetch("/api/v1/landing/stats").then(r => r.json()).then(setStats).catch(()=>{});
    fetch("/api/v1/map/points").then(r => r.json()).then(setMapData).catch(()=>{});
  }, []);
  React.useEffect(() => {
    if (user && (user.role === "guardian" || user.role === "citizen")) {
      // credentials 없으면 일부 브라우저/배포 환경(서로 다른 도메인 또는 쿠키 정책)에서
      // 세션 쿠키가 빠져 401이 떨어진다. landing의 다른 fetch들도 본인 컨텍스트가 필요한
      // 경우는 동일하게 same-origin을 명시.
      fetch("/api/v1/dashboard/guardian", { credentials: "same-origin" })
        .then(r => r.ok ? r.json() : null).then(setGuardian).catch(()=>{});
    }
  }, [user]);

  const heroTitle = guardian?.elderly?.name
    ? `오늘도 ${guardian.elderly.name} 어르신\n곁에서, 함께.`
    : "한 권역의 돌봄을\n하나의 앱에서.";

  return (
    <div className="app" style={{background:'var(--bg)', overflow:'auto', padding:'12px 0 0'}}>

      {/* 1. status bar — 목업 전용(가짜 시계·신호·배터리). 실기기에서는 OS 상태바와
           중복이라 좁은 뷰포트에서 숨긴다(layouts/design.html.erb 의 data-mock-chrome). */}
      <div data-mock-chrome style={{display:'flex', justifyContent:'space-between', padding:'6px 24px', fontSize:11, fontWeight:600, color:'var(--ink-1)'}}>
        <span>{new Date().toLocaleTimeString('ko-KR',{hour:'2-digit',minute:'2-digit'})}</span>
        <div style={{display:'flex', gap:5, alignItems:'center'}}>
          <span>●●●</span><span style={{fontSize:10}}>5G</span>
          <span style={{width:18, height:9, border:'1px solid var(--ink-1)', borderRadius:2, position:'relative'}}><span style={{position:'absolute', inset:1, background:'var(--ink-1)'}}/></span>
        </div>
      </div>

      {/* brand + login */}
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 22px 8px'}}>
        <a href="/" style={{display:'flex', alignItems:'center', gap:8, textDecoration:'none', color:'inherit'}}>
          <div className="brand-mark" style={{width:26, height:26, fontSize:11}}>{brandParts(siteName()).mark}</div>
          <div>
            <div style={{fontWeight:800, fontSize:13, lineHeight:1.1}}>{brandParts(siteName()).name}</div>
            {brandParts(siteName()).sub && <div style={{fontSize:9, color:'var(--ink-3)', letterSpacing:'0.06em'}}>{brandParts(siteName()).sub}</div>}
          </div>
        </a>
        {/* 로그인 칩 + 햄버거 메뉴(서비스 소개·주요 기능·통합돌봄법·도입 안내·고객지원).
            드로어 구성은 데스크톱 랜딩/공개 페이지와 동일한 MobileNavMenu 를 공유한다. */}
        <div style={{display:'flex', alignItems:'center', gap:8}}>
          {user ? (
            <a href={user.default_path} style={{fontSize:11, padding:'4px 10px', background:'var(--orange-50)', color:'var(--orange-700)', borderRadius:999, fontWeight:700, textDecoration:'none'}}>{user.name}님 →</a>
          ) : (
            <a href="/login" style={{fontSize:11, padding:'4px 10px', background:'var(--orange-50)', color:'var(--orange-700)', borderRadius:999, fontWeight:700, textDecoration:'none'}}>로그인</a>
          )}
          <MobileNavMenu compact/>
        </div>
      </div>

      {/* 2. hero */}
      <div style={{padding:'22px 22px 0'}}>
        <div style={{display:'inline-flex', alignItems:'center', gap:6, padding:'4px 10px', background:'var(--orange-50)', color:'var(--orange-700)', borderRadius:999, fontSize:10.5, fontWeight:700, marginBottom:12}}>
          <span className="dot" style={{background:'var(--orange-500)', width:5, height:5, display:'inline-block', borderRadius:99}}/> 2026 돌봄통합지원법 시행
        </div>
        <h1 style={{fontSize:28, fontWeight:800, lineHeight:1.2, letterSpacing:'-0.02em', margin:0, whiteSpace:'pre-line'}}>
          {heroTitle}
        </h1>
        <p style={{fontSize:12.5, color:'var(--ink-2)', lineHeight:1.65, marginTop:12}}>
          상태에 맞는 케어매니저를 AI가 찾아드립니다. 방문 시작과 마무리까지 가족이 함께 봅니다.
        </p>

        {/* live stats strip */}
        <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginTop:18, paddingTop:14, borderTop:'1px solid var(--border-soft)'}}>
          <MStat n={stats?.active_regions ?? '·'}                   l="권역"/>
          <MStat n={stats?.total_managers ?? '·'}                    l="매니저"/>
          <MStat n={stats ? `${stats.success_rate}%` : '·'}          l="성공률"/>
          <MStat n={stats ? fmtDuration(stats.avg_response_mins) : '·'}    l="응답"/>
        </div>
      </div>

      {/* 3. hero CTA card */}
      <div style={{margin:'22px 22px 0', borderRadius:20, padding:18, background:'linear-gradient(140deg, var(--orange-500), var(--orange-600))', color:'#fff', position:'relative', overflow:'hidden'}}>
        <div style={{position:'absolute', top:-30, right:-30, width:140, height:140, borderRadius:'50%', background:'rgba(255,255,255,0.1)'}}/>
        {guardian?.assigned_manager ? (
          <>
            <div style={{fontSize:11, fontWeight:600, opacity:.9}}>
              {guardian.assigned_manager.proposed ? '오늘 매칭 제안' : '오늘 일정'}
            </div>
            <div style={{fontSize:18, fontWeight:700, marginTop:6, lineHeight:1.3}}>
              {guardian.assigned_manager.name} 매니저<br/>
              <span style={{fontSize:13, opacity:.95}}>{guardian.assigned_manager.qualification}</span>
            </div>
            <div style={{display:'flex', alignItems:'center', gap:8, marginTop:12, paddingTop:12, borderTop:'1px solid rgba(255,255,255,.2)'}}>
              <div className="avatar sm" style={{background:'rgba(255,255,255,.25)'}}>{guardian.assigned_manager.name[0]}</div>
              <div style={{fontSize:11, opacity:.95}}>출발 전 알림이 전송됩니다</div>
            </div>
          </>
        ) : (
          <>
            <div style={{fontSize:11, fontWeight:600, opacity:.9}}>지금 시작하기</div>
            <div style={{fontSize:18, fontWeight:700, marginTop:6, lineHeight:1.3}}>회원가입으로 케어 신청 후<br/>매니저를 추천받으세요</div>
            <div style={{display:'flex', alignItems:'center', gap:8, marginTop:12, paddingTop:12, borderTop:'1px solid rgba(255,255,255,.2)'}}>
              <span style={{width:24, height:24, borderRadius:8, background:'rgba(255,255,255,.25)', display:'grid', placeItems:'center', color:'#fff', fontWeight:800, fontSize:11}}>AI</span>
              <div style={{fontSize:11, opacity:.95}}>AI가 6개 차원으로 평가합니다</div>
            </div>
          </>
        )}
      </div>

      {/* 4. quick actions */}
      <div style={{padding:'22px 22px 0', display:'grid', gridTemplateColumns:'1fr 1fr', gap:10}}>
        {[
          {t:'어르신 등록', s:'회원가입으로 신청', c:'orange', i:'user',   href:'/signup'},
          {t:'담당 매니저', s:'프로필 보기', c:'green',  i:'manager',  href: user ? '/dashboard/guardian' : '/login'},
          {t:'방문 일정',   s:'이번 주',      c:'amber',  i:'schedule', href: user ? '/m/timeline' : '/login'},
          {t:'알림함',      s:'24시간',       c:'rose',   i:'bell',     href:'/m/notify'},
        ].map((q,i)=>(
          <a key={i} href={q.href} className="card card-pad" style={{padding:14, borderRadius:14, textDecoration:'none', color:'inherit'}}>
            <span style={{width:32, height:32, borderRadius:10, background:`var(--${q.c}-50)`, color:`var(--${q.c}-${q.c==='amber'?'500':'600'})`, display:'grid', placeItems:'center'}}>
              <span style={{width:18,height:18,display:'inline-block'}}>{Ic[q.i]}</span>
            </span>
            <div style={{fontWeight:700, fontSize:13, marginTop:10}}>{q.t}</div>
            <div style={{fontSize:11, color:'var(--ink-3)'}}>{q.s}</div>
          </a>
        ))}
      </div>

      {/* 5. primary CTA */}
      <div style={{padding:'22px 22px 0'}}>
        <a href="/signup" className="btn btn-primary btn-lg" style={{width:'100%', textDecoration:'none', display:'flex', justifyContent:'center'}}>회원가입하고 케어 신청</a>
        {!user && (
          <div style={{textAlign:'center', fontSize:11, color:'var(--ink-3)', marginTop:10}}>
            이미 회원이신가요? <a href="/login" style={{color:'var(--ink-1)', fontWeight:700, textDecoration:'none'}}>로그인</a>
          </div>
        )}
      </div>

      {/* 6. features — horizontal scroll */}
      <div style={{padding:'30px 0 0'}}>
        <div style={{padding:'0 22px', marginBottom:10}}>
          <div style={{fontSize:10.5, fontWeight:700, color:'var(--orange-700)', letterSpacing:'0.08em'}}>WHAT WE DO</div>
          <h2 style={{fontSize:18, fontWeight:800, letterSpacing:'-0.02em', margin:'4px 0 0'}}>매일의 돌봄, 더 똑똑하게</h2>
        </div>
        <div style={{display:'flex', gap:10, padding:'8px 22px 4px', overflowX:'auto', scrollSnapType:'x mandatory'}}>
          {[
            {t:'AI 자동매칭 (COD)',  d:'어르신 상태·자격·거리·매니저 부하를 가중치로 평가해 최적 매니저를 추천합니다.', i:'match',   c:'orange'},
            {t:'권역 기반 운영',     d:'권역 협회 관리자가 케이스·기관·매니저를 한 화면에서 통합 운영합니다.',       i:'region',  c:'green' },
            {t:'지도·이동 최적화',   d:'유관기관·매니저 위치를 실시간으로 확인하고 동선을 줄입니다.',                i:'map',     c:'sky'   },
            {t:'보호자 안심 알림',   d:'방문 시작·종료·이상 신호를 가족에게 PWA로 즉시 알립니다.',                  i:'heart',   c:'rose'  },
          ].map((f,i)=>(
            <div key={i} className="card card-pad" style={{minWidth:240, flex:'0 0 240px', padding:14, borderRadius:14, scrollSnapAlign:'start'}}>
              <span style={{width:36, height:36, borderRadius:10, background:`var(--${f.c}-50)`, color:`var(--${f.c}-600)`, display:'grid', placeItems:'center'}}>
                <span style={{width:20, height:20, display:'inline-block'}}>{Ic[f.i]}</span>
              </span>
              <div style={{fontWeight:800, fontSize:14, marginTop:10}}>{f.t}</div>
              <div style={{fontSize:11.5, color:'var(--ink-2)', lineHeight:1.55, marginTop:6}}>{f.d}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 7. How it works */}
      <div style={{padding:'30px 22px 0'}}>
        <div style={{fontSize:10.5, fontWeight:700, color:'var(--orange-700)', letterSpacing:'0.08em'}}>HOW IT WORKS</div>
        <h2 style={{fontSize:18, fontWeight:800, letterSpacing:'-0.02em', margin:'4px 0 14px'}}>신청에서 방문까지, 3단계</h2>
        {[
          {n:'01', t:'회원가입',   d:'보호자·어르신으로 가입하며 어르신 정보를 등록합니다.', c:'orange'},
          {n:'02', t:'AI 매칭 추천', d:'권역 관리자가 검토하고 최적 매니저를 배정합니다.', c:'green'},
          {n:'03', t:'방문 · 알림',  d:'매니저가 출동하고 보호자에게 실시간으로 공유합니다.', c:'sky'},
        ].map((s,i)=>(
          <div key={i} style={{display:'flex', gap:14, padding:'14px 0', borderBottom: i<2?'1px solid var(--border-soft)':'none'}}>
            <div style={{flex:'0 0 44px', height:44, borderRadius:12, background:`var(--${s.c}-50)`, color:`var(--${s.c}-700)`, display:'grid', placeItems:'center', fontSize:13, fontWeight:800}}>{s.n}</div>
            <div style={{flex:1, minWidth:0}}>
              <div style={{fontSize:14, fontWeight:800}}>{s.t}</div>
              <div style={{fontSize:11.5, color:'var(--ink-2)', lineHeight:1.55, marginTop:3}}>{s.d}</div>
            </div>
          </div>
        ))}
      </div>

      {/* 8. mini live map */}
      <div style={{padding:'30px 22px 0'}}>
        <div style={{display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:10}}>
          <div>
            <div style={{fontSize:10.5, fontWeight:700, color:'var(--orange-700)', letterSpacing:'0.08em'}}>LIVE MAP</div>
            <h2 style={{fontSize:18, fontWeight:800, letterSpacing:'-0.02em', margin:'4px 0 0'}}>지금 권역 안에서</h2>
            <div style={{fontSize:11.5, color:'var(--ink-3)', marginTop:2}}>
              {mapData ? `기관 ${mapData.organizations.length} · 매니저 ${mapData.care_managers.length} · 어르신 ${mapData.elderlies.length}` : '실시간 로드 중…'}
            </div>
          </div>
          <a href="/map" style={{fontSize:11.5, fontWeight:700, color:'var(--orange-600)', textDecoration:'none'}}>크게 →</a>
        </div>
        <div className="card" style={{position:'relative', height:200, borderRadius:14, overflow:'hidden', background:'linear-gradient(160deg, #FFF8F0 0%, #FFEFE0 100%)'}}>
          <svg viewBox="0 0 100 60" preserveAspectRatio="none" style={{position:'absolute', inset:0, width:'100%', height:'100%'}}>
            <defs>
              <pattern id="mlGrid" width="10" height="10" patternUnits="userSpaceOnUse">
                <path d="M10 0 H0 V10" stroke="rgba(232,130,91,0.15)" strokeWidth="0.2" fill="none"/>
              </pattern>
            </defs>
            <rect width="100" height="60" fill="url(#mlGrid)"/>
          </svg>
          {mapData ? (() => {
            const all = [...mapData.organizations, ...mapData.care_managers, ...mapData.elderlies];
            if (all.length === 0) return null;
            const lats = all.map(p => p.lat), lngs = all.map(p => p.lng);
            const center = { lat: (Math.max(...lats)+Math.min(...lats))/2, lng: (Math.max(...lngs)+Math.min(...lngs))/2 };
            const project = (p) => {
              const dx = (p.lng - center.lng) * 5500;
              const dy = (center.lat - p.lat) * 5500;
              return { x: Math.max(6, Math.min(94, 50 + dx)), y: Math.max(10, Math.min(85, 50 + dy)) };
            };
            return (
              <>
                {mapData.organizations.slice(0, 10).map((o,i) => {
                  const c = CAT_COLOR[o.category] || 'orange';
                  const p = project(o);
                  return <span key={'o'+i} style={{position:'absolute', left:`${p.x}%`, top:`${p.y}%`, width:10, height:10, marginLeft:-5, marginTop:-5, borderRadius:3, background:`var(--${c}-500)`, boxShadow:'0 1px 3px rgba(0,0,0,0.15)'}}/>;
                })}
                {mapData.care_managers.slice(0, 14).map((m,i) => {
                  const p = project(m);
                  return <span key={'m'+i} style={{position:'absolute', left:`${p.x}%`, top:`${p.y}%`, width:8, height:8, marginLeft:-4, marginTop:-4, borderRadius:'50%', background:'var(--green-500)', border:'1.5px solid #fff', boxShadow:'0 1px 2px rgba(0,0,0,0.18)'}}/>;
                })}
                {mapData.elderlies.slice(0, 18).map((e,i) => {
                  const p = project(e);
                  return <span key={'e'+i} style={{position:'absolute', left:`${p.x}%`, top:`${p.y}%`, width:7, height:7, marginLeft:-3.5, marginTop:-3.5, borderRadius:'50%', background:'var(--orange-500)', opacity:0.7}}/>;
                })}
              </>
            );
          })() : (
            <div style={{position:'absolute', inset:0, display:'grid', placeItems:'center', color:'var(--ink-3)', fontSize:11}}>지도 로드 중…</div>
          )}
          {/* legend */}
          <div style={{position:'absolute', bottom:8, left:8, right:8, display:'flex', justifyContent:'space-between', fontSize:9.5, color:'var(--ink-2)', background:'rgba(255,255,255,0.85)', backdropFilter:'blur(6px)', borderRadius:8, padding:'5px 8px'}}>
            <Legend c="orange" l="어르신"/>
            <Legend c="green"  l="매니저"/>
            <Legend c="rose"   l="병원"/>
            <Legend c="sky"    l="복지"/>
          </div>
        </div>
      </div>

      {/* 9. partners / trust */}
      <div style={{padding:'30px 22px 0'}}>
        <div style={{fontSize:10.5, fontWeight:700, color:'var(--orange-700)', letterSpacing:'0.08em'}}>PARTNERS</div>
        <h2 style={{fontSize:18, fontWeight:800, letterSpacing:'-0.02em', margin:'4px 0 12px'}}>권역 안의 모든 기관과</h2>
        <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8}}>
          {[
            {l:'요양기관', c:'amber'},
            {l:'병원',     c:'rose'},
            {l:'보건소',   c:'green'},
            {l:'소방',     c:'orange'},
            {l:'복지센터', c:'sky'},
            {l:'행정복지', c:'sky'},
            {l:'장기요양', c:'amber'},
            {l:'협회',     c:'green'},
          ].map((p,i)=>(
            <div key={i} style={{padding:'10px 6px', borderRadius:10, background:`var(--${p.c}-50)`, color:`var(--${p.c}-700)`, textAlign:'center', fontSize:11, fontWeight:700}}>{p.l}</div>
          ))}
        </div>
        <div style={{marginTop:14, padding:14, background:'var(--bg-sub)', borderRadius:14, display:'flex', gap:12, alignItems:'center'}}>
          <span style={{width:36, height:36, borderRadius:10, background:'var(--surface)', color:'var(--orange-600)', display:'grid', placeItems:'center'}}>
            <span style={{width:20, height:20, display:'inline-block'}}>{Ic.approve}</span>
          </span>
          <div style={{flex:1, fontSize:11.5, lineHeight:1.55}}>
            <b style={{color:'var(--ink-1)'}}>2026 돌봄통합지원법</b> 시행에 따라<br/>
            <span style={{color:'var(--ink-3)'}}>권역별 요양기관협회와 공동 운영합니다.</span>
          </div>
        </div>
      </div>

      {/* 10. FAQ */}
      <div style={{padding:'30px 22px 0'}}>
        <div style={{fontSize:10.5, fontWeight:700, color:'var(--orange-700)', letterSpacing:'0.08em'}}>자주 묻는 질문</div>
        <h2 style={{fontSize:18, fontWeight:800, letterSpacing:'-0.02em', margin:'4px 0 12px'}}>처음이시라면</h2>
        {[
          {q:'케어매니저는 어떻게 정해지나요?',  a:'AI가 어르신 상태·매니저 자격·거리·부하·이전 관계·보호자 선호 6개 차원을 평가해 추천하고, 권역 관리자가 최종 배정합니다.'},
          {q:'비용은 어떻게 되나요?',             a:'장기요양보험 자격이 있는 경우 본인부담금만 청구됩니다. 신청 시 자격 확인이 자동으로 진행됩니다.'},
          {q:'가족이 같이 볼 수 있나요?',         a:'네. 보호자 PWA로 방문 시작·종료·이상 신호 알림을 실시간으로 받을 수 있습니다.'},
          {q:'개인정보는 안전한가요?',            a:'민감정보는 암호화되어 저장되며, 모든 접근은 감사 로그에 기록됩니다. 자세한 내용은 개인정보처리방침을 참고해 주세요.'},
        ].map((it, i) => (
          <div key={i} style={{borderBottom:'1px solid var(--border-soft)'}}>
            <button onClick={()=>setFaq(faq===i?-1:i)} style={{width:'100%', textAlign:'left', padding:'14px 0', display:'flex', alignItems:'center', gap:10, background:'transparent', border:'none', cursor:'pointer'}}>
              <span style={{flex:1, fontSize:13, fontWeight:700, color:'var(--ink-1)'}}>{it.q}</span>
              <span style={{width:18, height:18, display:'inline-block', transform: faq===i?'rotate(180deg)':'none', transition:'transform 0.15s', color:'var(--ink-3)'}}>{Ic.caret}</span>
            </button>
            {faq === i && (
              <div style={{padding:'0 0 14px', fontSize:12, color:'var(--ink-2)', lineHeight:1.65}}>{it.a}</div>
            )}
          </div>
        ))}
      </div>

      {/* 10-1. 더 알아보기 — 데스크톱 랜딩 상단 메뉴와 같은 공개 페이지들.
           모바일에는 상단 내비가 없어 이 목록이 유일한 진입점이다(공개 페이지는
           유동 레이아웃이라 좁은 화면에서도 열린다). */}
      <div style={{padding:'28px 22px 0'}}>
        <div style={{fontSize:10.5, fontWeight:700, color:'var(--orange-700)', letterSpacing:'0.08em'}}>더 알아보기</div>
        <h2 style={{fontSize:18, fontWeight:800, letterSpacing:'-0.02em', margin:'4px 0 12px'}}>서비스와 제도 안내</h2>
        <div style={{display:'flex', flexDirection:'column', gap:8}}>
          {[
            { l:'서비스 소개',      d:'무엇을 어떻게 바꾸는 서비스인지',   h:'/about',         i:'help'  },
            { l:'주요 기능',        d:'매칭·방문·기록·알림 기능 전체',     h:'/features',      i:'match' },
            { l:'2026 통합돌봄법',  d:'시행 내용과 준비 사항',             h:'/care-act-2026', i:'case'  },
            { l:'도입 안내',        d:'권역·기관 도입 절차와 상담 신청',   h:'/adopt',         i:'org'   },
            { l:'고객지원',         d:'자주 묻는 질문과 문의하기',         h:'/support',       i:'chat'  },
          ].map((m,i) => (
            <a key={i} href={m.h} style={{display:'flex', alignItems:'center', gap:12, padding:'12px 14px', borderRadius:14, border:'1px solid var(--border-soft)', background:'var(--surface)', textDecoration:'none', color:'inherit'}}>
              <span style={{width:32, height:32, borderRadius:10, background:'var(--orange-50)', color:'var(--orange-600)', display:'grid', placeItems:'center', flex:'0 0 32px', fontSize:17}}>
                <span style={{width:17, height:17, display:'inline-block'}}>{Ic[m.i]}</span>
              </span>
              <span style={{flex:1, minWidth:0}}>
                <span style={{display:'block', fontSize:13, fontWeight:700}}>{m.l}</span>
                <span style={{display:'block', fontSize:10.5, color:'var(--ink-3)', marginTop:2}}>{m.d}</span>
              </span>
              <span style={{width:14, height:14, display:'inline-block', color:'var(--ink-3)'}}>{Ic.arrow}</span>
            </a>
          ))}
        </div>
      </div>

      {/* 11. secondary CTA + legal */}
      <div style={{margin:'28px 22px 20px', padding:'20px 18px', borderRadius:18, background:'linear-gradient(160deg, #2A241F 0%, #3F342C 60%, #5A4434 100%)', color:'#F5EBD8', position:'relative', overflow:'hidden'}}>
        <div style={{position:'absolute', top:-40, right:-40, width:160, height:160, borderRadius:'50%', background:'rgba(232,130,91,0.18)'}}/>
        <div style={{fontSize:11, color:'#EFB287', fontWeight:700, letterSpacing:'0.08em'}}>지금 시작하세요</div>
        <div style={{fontSize:20, fontWeight:800, lineHeight:1.3, marginTop:8, color:'#fff'}}>
          한 분 한 분의 돌봄을<br/>가장 가까운 거리에서.
        </div>
        <a href="/signup" className="btn btn-primary btn-md" style={{marginTop:14, textDecoration:'none', display:'inline-flex'}}>회원가입하고 신청 →</a>
        <div style={{marginTop:14, paddingTop:14, borderTop:'1px solid rgba(255,255,255,0.12)', display:'flex', gap:14, fontSize:10.5, color:'#A89A7C'}}>
          <a href="/m/privacy" style={{color:'inherit', textDecoration:'none'}}>개인정보처리방침</a>
          <a href="/m/terms"   style={{color:'inherit', textDecoration:'none'}}>이용약관</a>
          <a href={`tel:${supportPhone()}`} style={{color:'inherit', textDecoration:'none', marginLeft:'auto'}}>{supportPhone()}</a>
        </div>
      </div>

      {/* 12. bottom nav */}
      <div style={{marginTop:'auto'}}>
        <AppFooterCompact/>
        <div style={{display:'flex', justifyContent:'space-around', padding:'8px 20px 14px', borderTop:'1px solid var(--border-soft)', background:'var(--surface)'}}>
          {[
            {l:'홈',    i:'dashboard', a:true, href:'/m'},
            {l:'일정',  i:'schedule', href:'/m/timeline'},
            {l:'매니저', i:'manager',  href: user ? '/m/guardian-manager' : '/login'},
            {l:'알림',  i:'bell',     href:'/m/notify'},
            {l:'마이',  i:'user',     href: user ? '/m/guardian-me' : '/login'},
          ].map((n,i)=>(
            <a key={i} href={n.href} style={{textAlign:'center', color: n.a ? 'var(--orange-600)' : 'var(--ink-3)', textDecoration:'none'}}>
              <span style={{width:22, height:22, display:'inline-block'}}>{Ic[n.i]}</span>
              <div style={{fontSize:9, fontWeight:700, marginTop:2}}>{n.l}</div>
            </a>
          ))}
        </div>
      </div>
      {/* 홈 인디케이터(목업 전용) */}
      <div data-mock-chrome style={{height:4, width:120, background:'var(--ink-1)', borderRadius:99, margin:'4px auto 8px'}}/>
    </div>
  );
}

function MStat({ n, l }) {
  return (
    <div style={{textAlign:'center'}}>
      <div style={{fontSize:15, fontWeight:800, color:'var(--ink-1)', fontVariantNumeric:'tabular-nums', lineHeight:1.1}}>{n}</div>
      <div style={{fontSize:9.5, color:'var(--ink-3)', marginTop:2, fontWeight:600}}>{l}</div>
    </div>
  );
}

function Legend({ c, l }) {
  return (
    <span style={{display:'inline-flex', alignItems:'center', gap:4}}>
      <span style={{width:6, height:6, borderRadius:'50%', background:`var(--${c}-500)`}}/>
      {l}
    </span>
  );
}

export { LandingMobile };
